/**
 * Business Types Data Module
 *
 * Purpose: Contains business type classifications and FSS requirements
 * Data Source: fss_business_categories.csv
 * Usage: IndustrySelector component, business type filtering
 */

const businessTypes = [
  {
    id: 'restaurant',
    name: 'Restaurant',
    category: 'Category 1',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Full-service dining establishment',
    highRisk: true
  },
  {
    id: 'cafe',
    name: 'Cafe',
    category: 'Category 1',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Coffee shop or casual dining',
    highRisk: true
  },
  {
    id: 'takeaway',
    name: 'Takeaway',
    category: 'Category 1',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Takeaway food service',
    highRisk: true
  },
  {
    id: 'catering',
    name: 'Catering',
    category: 'Category 1',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Catering and event food service',
    highRisk: true
  },
  {
    id: 'hotelPub',
    name: 'Hotel/Pub',
    category: 'Category 1',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Hotel or pub with food service',
    highRisk: true
  },
  {
    id: 'supermarket',
    name: 'Supermarket (with deli)',
    category: 'Category 2',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SIRRFSA001'],
    alternativeUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Supermarket with fresh food/deli section',
    highRisk: false
  },
  {
    id: 'bakery',
    name: 'Bakery',
    category: 'Category 1',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Bakery producing fresh baked goods',
    highRisk: true
  },
  {
    id: 'marketStall',
    name: 'Market Stall',
    category: 'Category 1 or 2',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SITXFSA005', 'SITXFSA006'],
    alternativeUnits: ['SIRRFSA001'],
    description: 'Market stall selling food products',
    highRisk: false
  },
  {
    id: 'mobileFoodVendor',
    name: 'Mobile Food Vendor',
    category: 'Category 1',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Food truck or mobile food service',
    highRisk: true
  },
  {
    id: 'hospital',
    name: 'Hospital',
    category: 'Category 1',
    industrySector: 'healthcare',
    fssRequired: true,
    recommendedUnits: ['HLTFSE001', 'HLTFSE005', 'HLTFSE007'],
    description: 'Hospital food service',
    highRisk: true,
    vulnerablePopulation: true
  },
  {
    id: 'agedCareFacility',
    name: 'Aged Care Facility',
    category: 'Category 1',
    industrySector: 'agedCareLicensed',
    fssRequired: true,
    recommendedUnits: ['HLTFSE001', 'HLTFSE005', 'HLTFSE007'],
    description: 'Aged care facility food service',
    highRisk: true,
    vulnerablePopulation: true,
    requiresSubSelection: true,
    subSelectionNote: 'Different requirements for licensed vs non-licensed facilities'
  },
  {
    id: 'childcareCentre',
    name: 'Childcare Centre',
    category: 'Category 1',
    industrySector: 'childcare',
    fssRequired: true,
    recommendedUnits: ['HLTFSE001', 'HLTFSE005', 'HLTFSE007'],
    description: 'Childcare or early learning centre',
    highRisk: true,
    vulnerablePopulation: true
  },
  {
    id: 'schoolCanteen',
    name: 'School Canteen',
    category: 'Category 1',
    industrySector: 'schoolsCanteens',
    fssRequired: true,
    recommendedUnits: ['HLTFSE001', 'HLTFSE005', 'HLTFSE007'],
    description: 'School canteen or tuckshop',
    highRisk: true,
    vulnerablePopulation: true
  },
  {
    id: 'foodProcessingPlant',
    name: 'Food Processing Plant',
    category: 'Category 1',
    industrySector: 'foodProcessing',
    fssRequired: true,
    recommendedUnits: ['FBPFSY1002', 'FBPFSY2002'],
    stateAvailability: ['VIC', 'QLD'],
    description: 'Food manufacturing or processing facility',
    highRisk: true,
    stateRestricted: true
  },
  {
    id: 'breweryWinery',
    name: 'Brewery/Winery',
    category: 'Category 1',
    industrySector: 'foodProcessing',
    fssRequired: true,
    recommendedUnits: ['FBPFSY1002', 'FBPFSY2002'],
    stateAvailability: ['VIC', 'QLD'],
    description: 'Brewery, winery, or beverage production',
    highRisk: true,
    stateRestricted: true
  },
  {
    id: 'serviceStation',
    name: 'Service Station (with food)',
    category: 'Category 2',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SIRRFSA001'],
    alternativeUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Service station with food retail',
    highRisk: false
  },
  {
    id: 'seafoodRetailer',
    name: 'Seafood Retailer',
    category: 'Category 2',
    industrySector: 'hospitalityRetail',
    fssRequired: true,
    recommendedUnits: ['SIRRFSA001'],
    alternativeUnits: ['SITXFSA005', 'SITXFSA006'],
    description: 'Fresh seafood retail business',
    highRisk: false
  }
];

export default businessTypes;
